import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { api } from '../api/client';
import { Logo } from '../components/Logo';
import { FlagWave } from '../components/FlagWave';
import { useAuth } from '../auth/AuthContext';
import { ThemeToggle } from '../theme/ThemeContext';
import { AiLauncher } from '../components/AiLauncher';
import { AiInvitation } from '../components/AiInvitation';

/**
 * Chrome for the signed-out catalog. Kept separate from AppShell: a visitor
 * browsing courses should not see an application sidebar built for members.
 *
 * The assistant is here as well as in AppShell, and opens itself, because the
 * catalogue is where the spoken greeting introduces it out loud. Which
 * assistant depends on who is looking: a signed-in visitor gets the real one,
 * and a signed-out visitor gets the introduction, because every question is
 * answered from a course they would have to be entitled to.
 */
export function PublicLayout() {
  const { user } = useAuth();
  const [pages, setPages] = useState<Array<{ slug: string; title: string }>>([]);

  useEffect(() => {
    api<{ items: Array<{ slug: string; title: string }> }>('/site/pages')
      .then((result) => setPages(result.items))
      .catch(() => setPages([]));
  }, []);

  return (
    <div className="pub">
      <header className="pub__bar">
        <div className="pub__brand">
          <Link to="/catalog" aria-label="SkillXzen home">
            {/* The mark waves here as it does on the login hero. This is the
                first SkillXzen screen most people meet, and the catalogue is
                otherwise a grid of other people's artwork. */}
            <FlagWave>
              <Logo variant="sidebar" tone="auto" />
            </FlagWave>
          </Link>
          <nav className="pub__nav">
            <NavLink to="/catalog" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              Courses
            </NavLink>
            <NavLink to="/faq" className={({ isActive }) => (isActive ? 'is-active' : '')}>
              FAQs
            </NavLink>
          </nav>
        </div>

        <div className="pub__actions">
          <ThemeToggle />
          {user ? (
            <Link className="btn btn--sm" to="/dashboard">
              Go to dashboard
            </Link>
          ) : (
            <>
              <Link className="btn btn--outline btn--sm" to="/login">
                Sign in
              </Link>
              <Link className="btn btn--sm" to="/register">
                Create account
              </Link>
            </>
          )}
        </div>
      </header>

      <main className="pub__body">
        <Outlet />
      </main>

      {/* After the footer in the markup, fixed in the layout: a dialog that
          opens by itself should not come before the page content in the tab
          order. */}
      {user ? <AiLauncher autoOpen /> : <AiInvitation />}

      <footer className="pub__foot">
        <div className="wrap pub__footwrap">
          <span>SkillXzen — Enterprise Learning &amp; Skill Development Platform</span>
          {/* Whatever Site Content has published, so a new page needs no deploy. */}
          <nav className="pub__footlinks" aria-label="Site">
            <Link to="/faq">FAQs</Link>
            {pages.map((page) => (
              <Link key={page.slug} to={`/pages/${page.slug}`}>
                {page.title}
              </Link>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}
